'use client'

import { useState, useMemo, useEffect, useRef } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { Search, X, Lightbulb } from 'lucide-react'

import { Group } from '@/core/api/entities/group'
import { Zone } from '@/core/api/entities/zone/zone'
import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import {
	useGroups,
	useAddSchedulesToGroup,
	useRemoveSchedulesFromGroup,
} from '@/core/hooks/useGroup'
import { useScheduleGroups, useScheduleZones } from '@/core/hooks/useSchedule'
import {
	useZones,
	useAddSchedulesToZone,
	useRemoveSchedulesFromZone,
} from '@/core/hooks/useZone'
import { useDebounce } from '@/core/hooks/utils/useDebounce'
import { useTranslation } from '@/core/i18n/useTranslation'
import { Button } from '@/shared/components/atoms/Button'
import { Input } from '@/shared/components/atoms/Input'
import { ScrollArea } from '@/shared/components/atoms/Scroll'
import { Separator } from '@/shared/components/atoms/Separator'
import {
	TabsBadge,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from '@/shared/components/atoms/Tabs'
import {
	BottomSheet,
	BottomSheetContent,
} from '@/shared/components/molecules/BottomSheet/BottomSheet'
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/shared/components/molecules/Dialog/dialog'
import { GroupRow } from '@/shared/components/molecules/GroupRow/GroupRow'
import { ModalPaginator } from '@/shared/components/molecules/ModalPaginator/ModalPaginator'
import { ZoneRow } from '@/shared/components/molecules/ZoneRow/ZoneRow'
import { useNotificationContext } from '@/shared/context/NotificationProvider'

type AssignTab = 'groups' | 'zones'
const DEFAULT_PAGE = 1
const DEFAULT_PAGE_SIZE = 20

interface AssignScheduleModalProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	scheduleId?: string
	scheduleName?: string
}

export const AssignScheduleModal = ({
	open,
	onOpenChange,
	scheduleId,
	scheduleName,
}: AssignScheduleModalProps) => {
	const { t } = useTranslation()
	const queryClient = useQueryClient()
	const { showNotification } = useNotificationContext()
	const [activeTab, setActiveTab] = useState<AssignTab>('zones')
	const [searchQuery, setSearchQuery] = useState('')
	const [groupsPage, setGroupsPage] = useState(DEFAULT_PAGE)
	const [zonesPage, setZonesPage] = useState(DEFAULT_PAGE)
	const [selectedGroups, setSelectedGroups] = useState<number[]>([])
	const [selectedZones, setSelectedZones] = useState<number[]>([])
	const [isAssigning, setIsAssigning] = useState(false)
	const [isDesktop, setIsDesktop] = useState(
		() => !window.matchMedia('(max-width: 767px)').matches
	)
	const [showHelpBanner, setShowHelpBanner] = useState(false)
	const initialSelectedGroupsRef = useRef<number[]>([])
	const initialSelectedZonesRef = useRef<number[]>([])
	const debouncedSearchQuery = useDebounce(searchQuery, 500)

	useEffect(() => {
		const mediaQuery = window.matchMedia('(max-width: 767px)')
		const handler = (e: MediaQueryListEvent) => setIsDesktop(!e.matches)
		mediaQuery.addEventListener('change', handler)
		return () => mediaQuery.removeEventListener('change', handler)
	}, [])

	// Fetch assigned groups
	const { data: assignedGroupsData, isLoading: isLoadingAssignedGroups } =
		useScheduleGroups({
			scheduleId,
			config: useAccountConfig({}),
			options: {
				enabled: open && !!scheduleId,
			},
		})

	// Fetch assigned zones
	const { data: assignedZonesData, isLoading: isLoadingAssignedZones } =
		useScheduleZones({
			scheduleId,
			config: useAccountConfig({}),
			options: {
				enabled: open && !!scheduleId,
			},
		})

	// Fetch all groups
	const { data: groupsResponse, isLoading: isLoadingGroups } = useGroups({
		config: useAccountConfig({
			params: {
				page: groupsPage,
				per_page: DEFAULT_PAGE_SIZE,
				query: debouncedSearchQuery || undefined,
			},
		}),
		options: {
			enabled: open,
		},
	})

	// Fetch all zones
	const { data: zonesResponse, isLoading: isLoadingZones } = useZones({
		config: useAccountConfig({
			params: {
				page: zonesPage,
				per_page: DEFAULT_PAGE_SIZE,
				query: debouncedSearchQuery || undefined,
				include: 'groups,store',
			},
		}),
		options: {
			enabled: open,
		},
	})

	const groups = groupsResponse?.data || []
	const zones = zonesResponse?.data || []
	const groupsTotalPages =
		groupsResponse?.meta?.pagination?.total_pages ??
		(groupsResponse?.meta as { total_pages?: number } | undefined)
			?.total_pages ??
		1
	const zonesTotalPages =
		zonesResponse?.meta?.pagination?.total_pages ??
		(zonesResponse?.meta as { total_pages?: number } | undefined)
			?.total_pages ??
		1

	useEffect(() => {
		setGroupsPage(DEFAULT_PAGE)
		setZonesPage(DEFAULT_PAGE)
	}, [debouncedSearchQuery])

	useEffect(() => {
		if (assignedGroupsData) {
			const assignedGroupIds = assignedGroupsData.map((g: Group) => g.id)
			setSelectedGroups(assignedGroupIds)
			initialSelectedGroupsRef.current = assignedGroupIds
		}
	}, [assignedGroupsData])

	useEffect(() => {
		if (assignedZonesData) {
			const assignedZoneIds = Array.isArray(assignedZonesData)
				? assignedZonesData.map((z: Zone) => z.id)
				: []
			setSelectedZones(assignedZoneIds)
			initialSelectedZonesRef.current = assignedZoneIds
		}
	}, [assignedZonesData])

	// Mutations for groups
	const { mutateAsync: addSchedulesToGroup } = useAddSchedulesToGroup({
		config: useAccountConfig({}),
		options: {
			onSuccess: () => {
				queryClient.invalidateQueries({ queryKey: ['group-schedules'] })
				queryClient.invalidateQueries({ queryKey: ['schedule-groups'] })
			},
		},
	})

	const { mutateAsync: removeSchedulesFromGroup } = useRemoveSchedulesFromGroup(
		{
			config: useAccountConfig({}),
			options: {
				onSuccess: () => {
					queryClient.invalidateQueries({ queryKey: ['group-schedules'] })
					queryClient.invalidateQueries({ queryKey: ['schedule-groups'] })
				},
			},
		}
	)

	// Mutations for zones
	const { mutateAsync: addSchedulesToZone } = useAddSchedulesToZone({
		config: useAccountConfig({}),
		options: {
			onSuccess: () => {
				queryClient.invalidateQueries({ queryKey: ['schedule-zones'] })
			},
		},
	})

	const { mutateAsync: removeSchedulesFromZone } = useRemoveSchedulesFromZone({
		config: useAccountConfig({}),
		options: {
			onSuccess: () => {
				queryClient.invalidateQueries({ queryKey: ['schedule-zones'] })
			},
		},
	})

	const handleToggleGroup = (groupId: number) => {
		setSelectedGroups((prev) => {
			if (prev.includes(groupId)) {
				return prev.filter((id) => id !== groupId)
			} else {
				return [...prev, groupId]
			}
		})
	}

	const handleToggleZone = (zoneId: number) => {
		setSelectedZones((prev) => {
			if (prev.includes(zoneId)) {
				return prev.filter((id) => id !== zoneId)
			} else {
				return [...prev, zoneId]
			}
		})
	}

	const handleAssign = async () => {
		if (!scheduleId) return

		setIsAssigning(true)

		try {
			const scheduleIdNumber = parseInt(scheduleId, 10)
			const initialGroups = initialSelectedGroupsRef.current
			const initialZones = initialSelectedZonesRef.current

			// Group changes
			const groupsToAdd = selectedGroups.filter(
				(groupId) => !initialGroups.includes(groupId)
			)
			const groupsToRemove = initialGroups.filter(
				(groupId) => !selectedGroups.includes(groupId)
			)

			// Zone changes - filter out zones that are in selected groups
			const filteredSelectedZones = selectedZones.filter(
				(zoneId) => !zonesInSelectedGroups.has(zoneId)
			)
			const zonesToAdd = filteredSelectedZones.filter(
				(zoneId) => !initialZones.includes(zoneId)
			)
			const zonesToRemove = initialZones.filter(
				(zoneId) => !filteredSelectedZones.includes(zoneId)
			)

			const promises: Promise<any>[] = []

			// Group operations
			groupsToAdd.forEach((groupId) =>
				promises.push(
					addSchedulesToGroup({
						groupId,
						data: { schedules: [scheduleIdNumber] },
					})
				)
			)
			groupsToRemove.forEach((groupId) =>
				promises.push(
					removeSchedulesFromGroup({
						groupId,
						data: { schedules: [scheduleIdNumber] },
					})
				)
			)

			// Zone operations
			zonesToAdd.forEach((zoneId) =>
				promises.push(
					addSchedulesToZone({
						zoneId,
						data: { schedules: [scheduleIdNumber] },
					})
				)
			)
			zonesToRemove.forEach((zoneId) =>
				promises.push(
					removeSchedulesFromZone({
						zoneId,
						data: { schedules: [scheduleIdNumber] },
					})
				)
			)

			await Promise.all(promises)

			const totalChanges =
				groupsToAdd.length +
				groupsToRemove.length +
				zonesToAdd.length +
				zonesToRemove.length

			if (totalChanges > 0) {
				let description = ''
				if (groupsToAdd.length > 0 && groupsToRemove.length > 0) {
					description = t('entities.schedule.notifications.added_and_removed', {
						name: scheduleName,
						addedCount: groupsToAdd.length,
						removedCount: groupsToRemove.length,
					})
				} else if (groupsToAdd.length > 0) {
					description = t('entities.schedule.notifications.added_to_groups', {
						name: scheduleName,
						count: groupsToAdd.length,
					})
				} else if (groupsToRemove.length > 0) {
					description = t(
						'entities.schedule.notifications.removed_from_groups',
						{
							name: scheduleName,
							count: groupsToRemove.length,
						}
					)
				}

				if (zonesToAdd.length > 0 || zonesToRemove.length > 0) {
					if (description) description += '. '
					if (zonesToAdd.length > 0) {
						description += t('entities.schedule.assign.added_to_zones', {
							name: scheduleName,
							count: zonesToAdd.length,
						})
					}
					if (zonesToRemove.length > 0) {
						if (zonesToAdd.length > 0) description += '. '
						description += t('entities.schedule.assign.removed_from_zones', {
							name: scheduleName,
							count: zonesToRemove.length,
						})
					}
				}

				showNotification({
					type: 'success',
					message: t('entities.schedule.notifications.updated_successfully'),
					description,
				})
			}

			setSelectedGroups([])
			setSelectedZones([])
			setSearchQuery('')
			initialSelectedGroupsRef.current = []
			initialSelectedZonesRef.current = []
			onOpenChange(false)
		} catch (error) {
			console.error('Error assigning schedule:', error)
			showNotification({
				type: 'error',
				message: t('entities.schedule.notifications.assignment_failed'),
				description: t(
					'entities.schedule.notifications.failed_to_update_assignments'
				),
			})
		} finally {
			setIsAssigning(false)
		}
	}

	const handleCancel = () => {
		setSelectedGroups([])
		setSelectedZones([])
		setSearchQuery('')
		setGroupsPage(DEFAULT_PAGE)
		setZonesPage(DEFAULT_PAGE)
		initialSelectedGroupsRef.current = []
		initialSelectedZonesRef.current = []
		onOpenChange(false)
	}

	const isLoading =
		isLoadingGroups ||
		isLoadingAssignedGroups ||
		isLoadingZones ||
		isLoadingAssignedZones

	// Compute zones that belong to selected groups
	const zonesInSelectedGroups = useMemo(() => {
		const zoneConflicts = new Map<number, string>() // Map zone ID to group name
		zones.forEach((zone) => {
			if (zone.groups && zone.groups.length > 0) {
				const conflictingGroup = zone.groups.find((group) =>
					selectedGroups.includes(group.id)
				)
				if (conflictingGroup) {
					zoneConflicts.set(zone.id, conflictingGroup.name)
				}
			}
		})
		return zoneConflicts
	}, [zones, selectedGroups])

	// Check if there are any changes to save
	const hasChanges = useMemo(() => {
		const initialGroups = initialSelectedGroupsRef.current
		const addedGroups = selectedGroups.filter(
			(id) => !initialGroups.includes(id)
		)
		const removedGroups = initialGroups.filter(
			(id) => !selectedGroups.includes(id)
		)
		const initialZones = initialSelectedZonesRef.current
		const addedZones = selectedZones.filter((id) => !initialZones.includes(id))
		const removedZones = initialZones.filter(
			(id) => !selectedZones.includes(id)
		)
		return (
			addedGroups.length > 0 ||
			removedGroups.length > 0 ||
			addedZones.length > 0 ||
			removedZones.length > 0
		)
	}, [selectedGroups, selectedZones])

	const content = (
		<>
			<div className="px-5 pt-2 pb-3 flex items-center justify-between">
				<h2 className="md:text-xl text-lg font-semibold">
					{isDesktop
						? t('entities.schedule.assign.title')
						: t('entities.schedule.assign.mobile_title')}
				</h2>
				{!isDesktop && (
					<Button
						type="button"
						variant="ghost"
						size="icon"
						onClick={handleCancel}
						className="h-9 w-9 rounded-full"
					>
						<X className="w-5 h-5" />
					</Button>
				)}
			</div>

			<Separator />

			<div className="px-5 py-3 flex flex-col flex-1 min-h-0 overflow-hidden">
				<Tabs
					value={activeTab}
					onValueChange={(value) => setActiveTab(value as AssignTab)}
					className="flex-1 flex flex-col min-h-0"
				>
					<TabsList className="flex-none mb-3">
						<TabsTrigger value="zones">
							{t('entities.schedule.assign.tab_zones')}
							<TabsBadge>{selectedZones.length}</TabsBadge>
						</TabsTrigger>
						<TabsTrigger value="groups">
							{t('entities.schedule.assign.tab_groups')}
							<TabsBadge>{selectedGroups.length}</TabsBadge>
						</TabsTrigger>
					</TabsList>

					{/* Zones Tab */}
					<TabsContent
						value="zones"
						className="mt-0 flex-1 flex flex-col min-h-0"
					>
						<div className="relative flex-none mb-3">
							<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
							<Input
								placeholder={t(
									'entities.schedule.assign.search_zones_placeholder'
								)}
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
								className="pl-10"
							/>
						</div>

						<ScrollArea className="flex-1 min-h-0 border rounded-md">
							{isLoading ? (
								<div className="flex items-center justify-center h-full text-muted-foreground text-sm">
									{t('entities.schedule.assign.loading_zones')}
								</div>
							) : zones.length === 0 ? (
								<div className="flex items-center justify-center h-full text-muted-foreground text-sm">
									{t('entities.schedule.assign.no_zones_found')}
								</div>
							) : (
								<div className="divide-y">
									{zones.map((zone: Zone) => {
										const groupName = zonesInSelectedGroups.get(zone.id)
										const isDisabled = !!groupName
										return (
											<ZoneRow
												key={`zone-${zone.id}`}
												zone={zone}
												isSelected={selectedZones.includes(zone.id)}
												onSelectionChange={() => handleToggleZone(zone.id)}
												disabled={isDisabled}
												disabledReason={
													isDisabled
														? t(
																'entities.schedule.assignment.zone_already_in_group',
																{ groupName }
															)
														: undefined
												}
											/>
										)
									})}
								</div>
							)}
						</ScrollArea>
						<ModalPaginator
							currentPage={zonesPage}
							totalPages={zonesTotalPages}
							onPageChange={setZonesPage}
						/>
					</TabsContent>

					{/* Groups Tab */}
					<TabsContent
						value="groups"
						className="mt-0 flex-1 flex flex-col min-h-0"
					>
						<div className="relative flex-none mb-3">
							<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
							<Input
								placeholder={t(
									'entities.schedule.assign.search_groups_placeholder'
								)}
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
								className="pl-10"
							/>
						</div>

						<ScrollArea className="flex-1 min-h-0 border rounded-md">
							{isLoading ? (
								<div className="flex items-center justify-center h-full text-muted-foreground text-sm">
									{t('entities.schedule.assign_to_group.loading')}
								</div>
							) : groups.length === 0 ? (
								<div className="flex items-center justify-center h-full text-muted-foreground text-sm">
									{t('entities.schedule.assign_to_group.no_groups_found')}
								</div>
							) : (
								<div className="divide-y">
									{groups.map((group: Group) => (
										<GroupRow
											key={`group-${group.id}`}
											group={group}
											isSelected={selectedGroups.includes(group.id)}
											onSelectionChange={() => handleToggleGroup(group.id)}
										/>
									))}
								</div>
							)}
						</ScrollArea>
						<ModalPaginator
							currentPage={groupsPage}
							totalPages={groupsTotalPages}
							onPageChange={setGroupsPage}
						/>
					</TabsContent>
				</Tabs>
			</div>

			<Separator />

			{/* Help Banner */}
			{showHelpBanner && (
				<>
					<div className="px-6 py-3 flex items-center justify-between bg-muted/30">
						{
							<div className="flex items-center gap-2">
								<Lightbulb className="h-4 w-4 text-muted-foreground" />
								<span className="text-sm text-muted-foreground">
									{activeTab === 'groups'
										? t('entities.schedule.assign_to_group.have_questions')
										: t(
												'entities.schedule.assign_to_zone.have_questions',
												'Have questions about zones?'
											)}
								</span>
							</div>
						}
						<div className="flex items-center gap-3">
							<Button variant="link" className="h-auto p-0 text-sm">
								{activeTab === 'groups'
									? t('entities.schedule.assign_to_group.read_docs')
									: t(
											'entities.schedule.assign_to_zone.read_docs',
											'Read docs'
										)}
							</Button>
							<Button
								variant="ghost"
								size="icon"
								className="h-6 w-6"
								onClick={() => setShowHelpBanner(false)}
							>
								<X className="h-4 w-4" />
							</Button>
						</div>
					</div>
					<Separator />
				</>
			)}
		</>
	)

	if (isDesktop) {
		return (
			<Dialog open={open} onOpenChange={onOpenChange}>
				<DialogContent
					className="max-w-3xl p-0 gap-0 flex flex-col h-[90vh] max-h-[90vh] overflow-hidden"
					onInteractOutside={(e) => e.preventDefault()}
				>
					<DialogHeader className="sr-only">
						<DialogTitle>{t('entities.schedule.assign.title')}</DialogTitle>
					</DialogHeader>
					{content}
					<DialogFooter className="px-6 py-4">
						<Button
							type="button"
							variant="outline"
							onClick={handleCancel}
							className="h-9 rounded-full px-6"
							disabled={isAssigning}
						>
							{t('actions.cancel')}
						</Button>
						<Button
							type="button"
							onClick={handleAssign}
							disabled={!hasChanges || isAssigning}
							className="h-9 rounded-full px-6"
							id="assign-schedule-modal-save-button"
							data-testid="assign-schedule-modal-save-button"
						>
							{isAssigning
								? t('entities.schedule.assign_to_group.saving')
								: t('entities.schedule.assign_to_group.save')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		)
	}

	return (
		<BottomSheet open={open} onOpenChange={onOpenChange}>
			<BottomSheetContent
				variant="card"
				className="border-border h-[86vh] max-h-[92vh] px-0 flex flex-col overflow-hidden"
			>
				<DialogHeader className="sr-only">
					<DialogTitle>{t('entities.schedule.assign.title')}</DialogTitle>
				</DialogHeader>
				{content}
				<div className="h-24 flex items-center justify-center px-4 pb-[calc(env(safe-area-inset-bottom)+12px)] pt-3 border-t border-border bg-card">
					<Button
						type="button"
						onClick={handleAssign}
						disabled={!hasChanges || isAssigning}
						className="rounded-full w-full"
						id="assign-schedule-modal-save-button"
						data-testid="assign-schedule-modal-save-button"
					>
						{isAssigning
							? t('entities.schedule.assign_to_group.saving')
							: t('entities.schedule.assign_to_group.save')}
					</Button>
				</div>
			</BottomSheetContent>
		</BottomSheet>
	)
}
